import { useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  useDiagnosticDepartments,
  useDiagnosticDashboard,
  useUpdateRoutingStatus,
} from '../../services/httpServices/queries';
import { diagnosticService } from '../../services/httpServices/diagnosticService';
import { printPdfBlobUrl } from '../../utils/printDocument';
import { joinFullName, patientInitials } from '../../utils/patientName';
import { DiagnosticSubmitModal } from '../../components/ui/DiagnosticSubmitModal';
import { usePermissions } from '../../hooks/usePermissions';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Activity,
  Loader2,
  AlertCircle,
  ArrowRight,
  X,
  ScanBarcode,
  Receipt,
  Printer,
  Search,
  CheckCircle,
  Plus,
  ClipboardList,
  Eye,
  Pencil,
  Send,
  FilterX,
  StickyNote,
} from 'lucide-react';

const statusMeta: Record<
  string,
  { label: string; badge: 'warning' | 'secondary' | 'primary' | 'success' | 'danger' | 'default'; icon?: React.ReactNode }
> = {
  PENDING: { label: 'Pending', badge: 'warning' },
  WAITING: { label: 'Waiting', badge: 'secondary' },
  IN_PROGRESS: { label: 'In Progress', badge: 'primary' },
  COMPLETED: { label: 'Completed', badge: 'success', icon: <CheckCircle size={12} /> },
  CANCELLED: { label: 'Cancelled', badge: 'danger' },
};

const nextStatusMap: Record<string, string> = {
  PENDING: 'WAITING',
  WAITING: 'IN_PROGRESS',
  IN_PROGRESS: 'COMPLETED',
};

export function DepartmentPage({ departmentId: departmentIdProp }: { departmentId?: string } = {}) {
  const params = useParams<{ departmentId: string }>();
  const departmentId = departmentIdProp ?? params.departmentId;
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scannedReceipt, setScannedReceipt] = useState<any>(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [submitModal, setSubmitModal] = useState<{
    open: boolean;
    receiptId: string;
    receiptNumber: string;
  } | null>(null);

  const { data: departmentsData, isLoading: deptLoading } = useDiagnosticDepartments(1, 100);
  const { data: dashboardData, isLoading, error } = useDiagnosticDashboard(departmentId);
  const updateMutation = useUpdateRoutingStatus();

  const departments = departmentsData?.data || [];
  const department = useMemo(
    () => departments.find((d: any) => d.id === departmentId),
    [departments, departmentId],
  );
  const routings = dashboardData?.data || [];

  const canManageRouting = hasPermission('diagnostics:routing:manage');
  const canUploadReport =
    hasPermission('diagnostics:receipt:update') ||
    hasPermission('diagnostics:receipt:create');

  const filteredRoutings = searchQuery
    ? routings.filter((r: any) => {
        const patient = r.receipt?.patient;
        const patientName = patient
          ? joinFullName(patient)
          : '';
        const receiptNumber = r.receipt?.receiptNumber || '';
        const testNames = (r.receipt?.items || [])
          .filter((item: any) => item.diagnosticDepartmentId === r.diagnosticDepartmentId)
          .map((item: any) => item.testName)
          .join(' ');
        const query = searchQuery.toLowerCase();
        return (
          patientName.toLowerCase().includes(query) ||
          receiptNumber.toLowerCase().includes(query) ||
          testNames.toLowerCase().includes(query)
        );
      })
    : routings;

  const displayedRoutings = selectedStatus
    ? filteredRoutings.filter((r: any) => r.status === selectedStatus)
    : filteredRoutings;

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {
      ALL: routings.length,
      PENDING: 0,
      WAITING: 0,
      IN_PROGRESS: 0,
      COMPLETED: 0,
      CANCELLED: 0,
    };
    routings.forEach((r: any) => {
      if (counts[r.status] !== undefined) counts[r.status]++;
    });
    return counts;
  }, [routings]);

  const handleScanReceipt = async () => {
    if (!barcodeInput.trim()) return;
    setScanLoading(true);
    setScanError(null);
    setScannedReceipt(null);
    try {
      const result = await diagnosticService.scanBarcode(barcodeInput.trim());
      setScannedReceipt(result.data);
    } catch (err: any) {
      setScanError(err?.message || 'Receipt not found');
    } finally {
      setScanLoading(false);
    }
  };

  const handlePrintTestBarcodes = async (receiptId: string) => {
    try {
      const url = await diagnosticService.getTestBarcodesPdf(receiptId);
      await printPdfBlobUrl(url);
    } catch (err: any) {
      setScanError(err?.message || 'Failed to print');
    }
  };

  const handlePrintSpecimenLabels = async (specimens: any[]) => {
    if (!specimens || specimens.length === 0) return;
    try {
      const url = await diagnosticService.getSpecimenLabelsPdf(specimens.map((s) => s.id));
      await printPdfBlobUrl(url);
    } catch (err: any) {
      setScanError(err?.message || 'Failed to print specimen labels');
    }
  };

  const handleGenerateSpecimenPlan = async (receiptId: string) => {
    try {
      const res = await diagnosticService.planReceiptSpecimens(receiptId);
      setScannedReceipt((prev: any) => (prev ? { ...prev, specimens: res.data } : prev));
    } catch (err: any) {
      setScanError(err?.message || 'Failed to generate specimen plan');
    }
  };

  const handleStatusUpdate = async (routingId: string, currentStatus: string) => {
    const nextStatus = nextStatusMap[currentStatus];
    if (!nextStatus) return;
    await updateMutation.mutateAsync({
      routingId,
      data: { status: nextStatus },
    });
  };

  const handleCancel = async (routingId: string) => {
    await updateMutation.mutateAsync({
      routingId,
      data: { status: 'CANCELLED' },
    });
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedStatus('');
  };

  if (deptLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!department) {
    return (
      <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
        <AlertCircle size={18} />
        <span>Department not found</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Activity className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1
              className="text-xl font-bold text-text cursor-pointer hover:text-primary transition-colors"
              onClick={() => navigate(`/diagnostics/routing/${department.id}`)}
            >
              {department.name}
            </h1>
            <p className="text-xs text-text-muted">Diagnostics Department Dashboard</p>
          </div>
        </div>
        {hasPermission('diagnostics:receipt:create') && (
          <Link
            to="/opd/diagnostic-receipts/new"
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
          >
            <Plus size={16} />
            New Receipt
          </Link>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { key: 'ALL', label: 'Total', color: 'bg-text-muted/10 text-text-muted border-text-muted/20' },
          { key: 'PENDING', label: 'Pending', color: 'bg-warning/10 text-warning border-warning/20' },
          { key: 'WAITING', label: 'Waiting', color: 'bg-info/10 text-info border-info/20' },
          { key: 'IN_PROGRESS', label: 'In Progress', color: 'bg-primary/10 text-primary border-primary/20' },
          { key: 'COMPLETED', label: 'Completed', color: 'bg-success/10 text-success border-success/20' },
          { key: 'CANCELLED', label: 'Cancelled', color: 'bg-danger/10 text-danger border-danger/20' },
        ].map((stat) => (
          <button
            key={stat.key}
            onClick={() => setSelectedStatus(selectedStatus === stat.key ? '' : stat.key)}
            className={`text-left p-3 rounded-xl border transition-all ${stat.color} ${
              selectedStatus === stat.key ? 'ring-2 ring-primary/30 shadow-sm scale-[1.02]' : 'hover:shadow-sm'
            }`}
          >
            <p className="text-lg font-bold">{statusCounts[stat.key] || 0}</p>
            <p className="text-xs font-medium opacity-90">{stat.label}</p>
          </button>
        ))}
      </div>

      {/* Barcode Scan */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <ScanBarcode className="w-4 h-4 text-primary" />
            <CardTitle className="text-base">Receipt Verification</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder="Scan or type receipt barcode..."
              value={barcodeInput}
              onChange={(e) => setBarcodeInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleScanReceipt()}
              className="flex-1"
            />
            {hasPermission('diagnostics:receipt:scan_barcode') && (
              <Button
                onClick={handleScanReceipt}
                disabled={scanLoading || !barcodeInput.trim()}
                size="sm"
              >
                {scanLoading ? (
                  <Loader2 size={14} className="animate-spin mr-1" />
                ) : (
                  <ScanBarcode size={14} className="mr-1" />
                )}
                Scan
              </Button>
            )}
          </div>

          {scanError && (
            <div className="flex items-center gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
              <AlertCircle size={16} />
              <span>{scanError}</span>
            </div>
          )}

          {scannedReceipt && (
            <div className="border border-border rounded-lg p-4 space-y-3 bg-background/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Receipt size={16} className="text-primary" />
                  <span className="text-sm font-semibold text-text">
                    {scannedReceipt.receiptNumber}
                  </span>
                  <span className="text-xs text-text-muted">
                    {joinFullName(scannedReceipt.patient || {})}
                  </span>
                </div>
                {hasPermission('diagnostics:receipt:print') && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePrintTestBarcodes(scannedReceipt.id)}
                  >
                    <Printer size={12} className="mr-1" />
                    Print Barcodes
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {scannedReceipt.items
                  ?.filter((item: any) => !item.isAccessory && item.orderBarcode)
                  .map((item: any) => (
                    <div
                      key={item.id}
                      className="bg-surface rounded-lg p-3 border border-border"
                    >
                      <p className="text-xs font-medium text-text">{item.testName}</p>
                      <p className="text-[10px] text-text-muted font-mono mt-1">
                        {item.orderBarcode}
                      </p>
                    </div>
                  ))}
              </div>

              <div className="border-t border-border pt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-text">
                    Specimens ({scannedReceipt.specimens?.length || 0})
                  </span>
                  {(scannedReceipt.specimens || []).length === 0
                    ? hasPermission('diagnostics:specimen:create') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleGenerateSpecimenPlan(scannedReceipt.id)}
                        >
                          <Printer size={12} className="mr-1" />
                          Generate Specimen Plan
                        </Button>
                      )
                    : hasPermission('diagnostics:specimen:print') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePrintSpecimenLabels(scannedReceipt.specimens || [])}
                        >
                          <Printer size={12} className="mr-1" />
                          Print Specimen Labels
                        </Button>
                      )}
                </div>
                {(scannedReceipt.specimens || []).length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {(scannedReceipt.specimens || []).map((s: any) => (
                      <div key={s.id} className="bg-surface rounded-lg p-3 border border-border">
                        <p className="text-xs font-medium text-text">
                          {s.specimenContainerCode || 'Container'}
                          {s.containerColor ? ` · ${s.containerColor}` : ''}
                        </p>
                        <p className="text-[10px] text-text-muted font-mono mt-1">{s.specimenBarcode}</p>
                        <p className="text-[10px] text-text-muted mt-1">
                          {(s.items || [])
                            .map((si: any) => si.receiptItem?.testName)
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Search & Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <Input
            placeholder="Search by patient, receipt, or test..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        {(searchQuery || selectedStatus) && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <FilterX size={14} className="mr-1" />
            Clear
          </Button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load department data</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/60">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-muted uppercase tracking-wide">
                    Receipt
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-muted uppercase tracking-wide">
                    Patient
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-muted uppercase tracking-wide">
                    Tests
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-muted uppercase tracking-wide">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-muted uppercase tracking-wide">
                    Room
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-text-muted uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {displayedRoutings.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center">
                      <ClipboardList className="w-10 h-10 mx-auto mb-3 text-text-muted/40" />
                      <p className="text-sm font-medium text-text-muted mb-1">
                        No requests found
                      </p>
                      <p className="text-xs text-text-muted/70">
                        {searchQuery || selectedStatus
                          ? 'Try adjusting your search or filters'
                          : 'No pending requests for this department'}
                      </p>
                      {(searchQuery || selectedStatus) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3"
                          onClick={clearFilters}
                        >
                          <FilterX size={14} className="mr-1" />
                          Clear Filters
                        </Button>
                      )}
                    </td>
                  </tr>
                ) : (
                  displayedRoutings.map((routing: any) => {
                    const receipt = routing.receipt;
                    const items =
                      receipt?.items?.filter(
                        (item: any) =>
                          item.diagnosticDepartmentId === routing.diagnosticDepartmentId,
                      ) || [];
                    const nextStatus = nextStatusMap[routing.status];
                    const meta = statusMeta[routing.status] || {
                      label: routing.status,
                      badge: 'default',
                    };

                    return (
                      <tr
                        key={routing.id}
                        className="hover:bg-background/40 transition-colors group"
                      >
                        <td className="px-4 py-3">
                          <div
                            className="flex flex-col cursor-pointer"
                            onClick={() => navigate(`/diagnostics/routing/${routing.id}`)}
                          >
                            <span className="text-sm font-mono text-primary hover:underline">
                              {receipt?.receiptNumber}
                            </span>
                            <span className="text-xs text-text-muted">
                              ৳{receipt?.totalAmount?.toLocaleString()}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-semibold text-primary">
                                {patientInitials(receipt?.patient || {})}
                              </span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-text">
                                {joinFullName(receipt?.patient || {})}
                              </span>
                              <span className="text-xs text-text-muted">
                                {receipt?.patient?.mobile}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1.5">
                            {items.map((item: any) => (
                              <Badge
                                key={item.id}
                                variant={
                                  item.status === 'COMPLETED'
                                    ? 'success'
                                    : item.status === 'REFUNDED'
                                      ? 'danger'
                                      : 'warning'
                                }
                              >
                                {item.testName}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={meta.badge} className="gap-1">
                            {meta.icon}
                            {meta.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-text-muted">
                          {routing.roomNumber || (
                            <span className="text-text-muted/50 italic">N/A</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => navigate(`/diagnostics/routing/${routing.id}`)}
                              className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                              title="View Order"
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              onClick={() => navigate(`/diagnostics/routing/${routing.id}`)}
                              className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                              title="Edit Order"
                            >
                              <Pencil size={16} />
                            </button>
                            {routing.departmentNotes && (
                              <div
                                className="p-1.5 rounded-lg text-text-muted hover:text-primary hover:bg-background transition-colors cursor-help"
                                title={`Notes: ${routing.departmentNotes}`}
                              >
                                <StickyNote size={16} />
                              </div>
                            )}
                            {canUploadReport && (
                              <button
                                onClick={() =>
                                  setSubmitModal({
                                    open: true,
                                    receiptId: receipt?.id,
                                    receiptNumber: receipt?.receiptNumber,
                                  })
                                }
                                className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                                title="Submit Doc & Data"
                              >
                                <Send size={16} />
                              </button>
                            )}
                            {nextStatus && canManageRouting && (
                              <Button
                                size="sm"
                                onClick={() => handleStatusUpdate(routing.id, routing.status)}
                                disabled={updateMutation.isPending}
                                className="ml-1"
                              >
                                <ArrowRight size={12} className="mr-1" />
                                {nextStatus.replace('_', ' ')}
                              </Button>
                            )}
                            {routing.status !== 'CANCELLED' &&
                              routing.status !== 'COMPLETED' &&
                              canManageRouting && (
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => handleCancel(routing.id)}
                                  disabled={updateMutation.isPending}
                                  className="ml-1"
                                >
                                  <X size={12} className="mr-1" />
                                  Cancel
                                </Button>
                              )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {submitModal?.open && (
        <DiagnosticSubmitModal
          entityType="diagnostic_receipt"
          entityId={submitModal.receiptId}
          entityName={submitModal.receiptNumber}
          onClose={() => setSubmitModal(null)}
        />
      )}
    </div>
  );
}
